/**
 * What a job LOOKS like: its kind, its icon, and the floating item.
 *
 * (The per-kind SVG medals were removed again at Janeks's request: the only art
 * on a job is the real item it pays, plus the hunted face for a bounty.)
 *
 * The board only tells us the counter's `action`, so the kind is derived from
 * that. Today's pool is five counters (sell, craft, talk, search, raid);
 * `bounty` and `delivery` are recognised by name so the day the pool grows a
 * hunt or a hand-over, it already draws correctly instead of falling back to
 * the generic mark.
 */
export type JobKind = 'sell' | 'craft' | 'talk' | 'search' | 'raid' | 'bounty' | 'delivery' | 'generic';

export function jobKind(action: string): JobKind {
  const a = action.toLowerCase();
  if (/bounty|kill|hunt/.test(a)) return 'bounty';
  if (/deliver|hand_?over|fetch/.test(a)) return 'delivery';
  if (a === 'item_sold' || /sold|sell/.test(a)) return 'sell';
  if (/craft/.test(a)) return 'craft';
  if (/talk/.test(a)) return 'talk';
  if (/search|loot|container/.test(a)) return 'search';
  if (/raid|extract/.test(a)) return 'raid';
  return 'generic';
}

/** Shown for a bounty until the board names the target: a hostile silhouette. */
export const BOUNTY_PREVIEW = '/portraits/npc-b.png';

const ICON_MODULES = import.meta.glob<string>('../assets/icons/*_icon.{png,webp}', {
  eager: true, query: '?url', import: 'default',
});
const ICON_BY_FILE: Record<string, string> = {};
for (const [path, url] of Object.entries(ICON_MODULES)) {
  ICON_BY_FILE[path.slice(path.lastIndexOf('/') + 1)] = url;
}

/**
 * The art for an item key, or null.
 *
 * The board carries keys, not `iconPath`s, and the player may not own the item
 * (it is a reward), so the inventory sheet cannot be asked. The catalog's
 * convention is `<key>_icon.webp|png`; keys that break it simply get no
 * floating item rather than a broken image.
 */
export function itemIconUrl(itemKey: string): string | null {
  return ICON_BY_FILE[`${itemKey}_icon.webp`] ?? ICON_BY_FILE[`${itemKey}_icon.png`] ?? null;
}
